import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";
import { LanguageToggle } from "./LanguageToggle";
import { useContactDialog } from "./ContactTrigger";
import { useLanguage } from "@/contexts/LanguageContext";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { openDialog } = useContactDialog();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const scrollToTop = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const scrollToSection = (id: string) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border" aria-label="Main navigation">
      <div className="container mx-auto px-4 md:px-6 py-4 md:py-5">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => {
              scrollToTop();
              setIsMenuOpen(false);
            }}
            className="text-sm md:text-2xl font-black tracking-tight hover:text-primary transition-colors"
            aria-label="Conventure home"
          >
            CONVENTURE<sup className="text-[10px] md:text-xs">™</sup>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection("about")}
              className="text-sm font-semibold text-foreground hover:text-primary transition-colors uppercase tracking-wide"
            >
              {t("nav.about")}
            </button>
            <button
              onClick={() => scrollToSection("services")}
              className="text-sm font-semibold text-foreground hover:text-primary transition-colors uppercase tracking-wide"
            >
              {t("nav.services")}
            </button>
            <button
              onClick={() => scrollToSection("sectors")}
              className="text-sm font-semibold text-foreground hover:text-primary transition-colors uppercase tracking-wide"
            >
              {t("nav.sectors")}
            </button>
            <button
              onClick={() => navigate("/for-startups")}
              className="text-sm font-semibold text-foreground hover:text-primary transition-colors uppercase tracking-wide"
            >
              {t("nav.forStartups")}
            </button>
            <Button size="sm" onClick={openDialog}>
              {t("nav.contact")}
            </Button>
            <LanguageToggle />
            <ThemeToggle />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle />
            <button
              className="p-1.5"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-6 pb-4 flex flex-col gap-4">
            <button
              onClick={() => scrollToSection("about")}
              className="text-left text-sm font-semibold text-foreground hover:text-primary transition-colors uppercase tracking-wide"
            >
              {t("nav.about")}
            </button>
            <button
              onClick={() => scrollToSection("services")}
              className="text-left text-sm font-semibold text-foreground hover:text-primary transition-colors uppercase tracking-wide"
            >
              {t("nav.services")}
            </button>
            <button
              onClick={() => scrollToSection("sectors")}
              className="text-left text-sm font-semibold text-foreground hover:text-primary transition-colors uppercase tracking-wide"
            >
              {t("nav.sectors")}
            </button>
            <button
              onClick={() => {
                navigate("/for-startups");
                setIsMenuOpen(false);
              }}
              className="text-left text-sm font-semibold text-foreground hover:text-primary transition-colors uppercase tracking-wide"
            >
              {t("nav.forStartups")}
            </button>
            <Button size="sm" onClick={openDialog} className="w-full">
              {t("nav.contact")}
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
